import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { aboutContent } from '@/mocks/articles';

const FORM_SUBMIT_URL = 'https://readdy.ai/api/form/d9n4c6e8mbnljin59ti0';

export default function About() {
  const { brandIntro, reputation, contact } = aboutContent;

  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleContactSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.querySelector('input[name="phone_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      form.reset();
      setTimeout(() => setFormStatus('idle'), 3000);
      return;
    }

    setFormStatus('submitting');
    setFormError('');

    try {
      const formData = new FormData(form);
      formData.delete('phone_alt');

      const response = await fetch(FORM_SUBMIT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed: Record<string, unknown> | null = null;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      const serverMsg: string | undefined = (parsed as Record<string, unknown> | null)?.meta
        ? (parsed as Record<string, { message?: string }>).meta?.message
        : (parsed as Record<string, string> | null)?.message;

      if (response.ok && parsed && (parsed as Record<string, string>).code === 'OK') {
        setFormStatus('success');
        form.reset();
        setTimeout(() => setFormStatus('idle'), 3000);
      } else {
        setFormStatus('error');
        setFormError(serverMsg || '提交失败，请稍后重试');
        setTimeout(() => setFormStatus('idle'), 3000);
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请稍后重试');
      setTimeout(() => setFormStatus('idle'), 3000);
    }
  };

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, i) => (
      <i
        key={i}
        className={`text-sm ${i < rating ? 'ri-star-fill text-accent-500' : 'ri-star-line text-foreground-300'}`}
      ></i>
    ));
  };

  return (
    <div className="min-h-screen bg-background-50">
      <ScrollToTop />
      <Navbar />
      <Breadcrumb />

      <main>
        {/* About Hero */}
        <section className="relative w-full py-16 md:py-24 overflow-hidden" aria-label="关于密桃视频">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=A%20warm%20cinematic%20background%20with%20soft%20bokeh%20and%20film%20reel%20texture%2C%20elegant%20dark%20atmosphere%20with%20golden%20accents%2C%20abstract%20movie%20industry%20aesthetic%2C%20warm%20tones&width=1920&height=500&seq=about-hero-bg&orientation=landscape"
              alt=""
              className="w-full h-full object-cover"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/60"></div>
          <div className="relative z-10 text-center px-4 max-w-3xl mx-auto">
            <h1 className="text-2xl md:text-4xl font-heading font-bold text-background-50 mb-4">
              关于密桃视频
            </h1>
            <p className="text-sm md:text-base text-background-50/70 leading-relaxed">
              聚合优质内容，畅享高清观影 —— 密桃视频汇聚红桃视频、樱桃视频、茄子视频、糖心视频等
              热门平台资源，为您提供一站式的免费视频观看体验。
            </p>
          </div>
        </section>

        {/* Brand Introduction */}
        <section className="max-w-[1200px] mx-auto px-4 md:px-6 py-10 md:py-14" aria-label="品牌介绍">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-center">
            <div>
              <h2 className="text-lg md:text-2xl font-heading font-bold text-foreground-900 mb-4 flex items-center gap-2">
                <i className="ri-building-2-line text-primary-500"></i>
                {brandIntro.title}
              </h2>
              <div className="text-sm text-foreground-600 leading-relaxed space-y-3">
                {brandIntro.content.split('\n\n').map((paragraph, index) => (
                  <p key={index}>{paragraph}</p>
                ))}
              </div>
            </div>
            <div className="rounded-lg overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=A%20modern%20cozy%20office%20space%20with%20large%20screen%20displaying%20film%20content%2C%20warm%20lighting%2C%20creative%20workspace%2C%20plants%2C%20collaborative%20atmosphere%2C%20professional%20photography%2C%20natural%20elements&width=800&height=500&seq=about-brand-img&orientation=landscape"
                alt="密桃视频办公环境"
                title="密桃视频品牌形象"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="bg-background-100 py-10 md:py-14" aria-label="平台数据">
          <div className="max-w-[1200px] mx-auto px-4 md:px-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
              {[
                { value: '10,000+', label: '高清影片', icon: 'ri-film-line' },
                { value: '500万+', label: '日活跃用户', icon: 'ri-user-line' },
                { value: '99.9%', label: '播放成功率', icon: 'ri-check-double-line' },
                { value: '4', label: '聚合源站', icon: 'ri-links-line' },
              ].map((stat, index) => (
                <div key={index} className="text-center p-5 bg-background-50 rounded-lg border border-background-200/70">
                  <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-100">
                    <i className={`${stat.icon} text-primary-500 text-xl`}></i>
                  </div>
                  <p className="text-xl md:text-2xl font-heading font-bold text-foreground-900">{stat.value}</p>
                  <p className="text-xs text-foreground-500 mt-1">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Reputation */}
        <section className="max-w-[1200px] mx-auto px-4 md:px-6 py-10 md:py-14" aria-label="用户评价">
          <h2 className="text-lg md:text-2xl font-heading font-bold text-foreground-900 mb-6 text-center flex items-center justify-center gap-2">
            <i className="ri-chat-heart-line text-accent-500"></i>
            {reputation.title}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {reputation.testimonials.map((testimonial, index) => (
              <div key={index} className="bg-background-100 rounded-lg p-5 border border-background-200/70 hover-lift">
                <div className="flex items-center gap-1 mb-3">
                  {renderStars(testimonial.rating)}
                </div>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  "{testimonial.content}"
                </p>
                <div className="flex items-center gap-3 pt-3 border-t border-background-200/70">
                  <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                    <i className="ri-user-3-line text-primary-500"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-800">{testimonial.name}</p>
                    <p className="text-xs text-foreground-400">{testimonial.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Contact */}
        <section className="bg-background-100 py-10 md:py-14" aria-label="联系我们" id="contact">
          <div className="max-w-[1200px] mx-auto px-4 md:px-6">
            <h2 className="text-lg md:text-2xl font-heading font-bold text-foreground-900 mb-6 text-center flex items-center justify-center gap-2">
              <i className="ri-mail-line text-primary-500"></i>
              {contact.title}
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Contact Info */}
              <div className="space-y-4">
                {[
                  { icon: 'ri-mail-line', label: '电子邮箱', value: contact.email },
                  { icon: 'ri-wechat-line', label: '微信', value: contact.wechat },
                  { icon: 'ri-qq-line', label: 'QQ', value: contact.qq },
                  { icon: 'ri-phone-line', label: '客服电话', value: contact.phone },
                  { icon: 'ri-map-pin-line', label: '公司地址', value: contact.address },
                  { icon: 'ri-time-line', label: '工作时间', value: contact.workingHours },
                ].map((item, index) => (
                  <div key={index} className="flex items-start gap-3 p-3 bg-background-50 rounded-lg">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 flex-shrink-0">
                      <i className={`${item.icon} text-primary-500`}></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-400 mb-0.5">{item.label}</p>
                      <p className="text-sm font-medium text-foreground-800">{item.value}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Contact Form */}
              <div className="bg-background-50 rounded-lg p-5 md:p-6 border border-background-200/70">
                <h3 className="text-base font-heading font-bold text-foreground-900 mb-4">发送消息</h3>
                <form onSubmit={handleContactSubmit} data-readdy-form="" className="space-y-4">
                  <div>
                    <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      姓名 <span className="text-primary-500">*</span>
                    </label>
                    <input
                      id="contact-name"
                      name="name"
                      type="text"
                      required
                      className="w-full px-3 py-2.5 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-800 placeholder:text-foreground-300 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors"
                      placeholder="请输入您的姓名"
                    />
                  </div>

                  <div>
                    <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      邮箱 <span className="text-primary-500">*</span>
                    </label>
                    <input
                      id="contact-email"
                      name="email"
                      type="email"
                      required
                      className="w-full px-3 py-2.5 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-800 placeholder:text-foreground-300 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors"
                      placeholder="请输入您的邮箱"
                    />
                  </div>

                  <div>
                    <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      主题
                    </label>
                    <input
                      id="contact-subject"
                      name="subject"
                      type="text"
                      className="w-full px-3 py-2.5 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-800 placeholder:text-foreground-300 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors"
                      placeholder="请输入消息主题"
                    />
                  </div>

                  <div>
                    <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      消息 <span className="text-primary-500">*</span>
                    </label>
                    <textarea
                      id="contact-message"
                      name="message"
                      required
                      maxLength={500}
                      rows={4}
                      className="w-full px-3 py-2.5 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-800 placeholder:text-foreground-300 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors resize-none"
                      placeholder="请输入您的消息（最多500字）"
                    ></textarea>
                  </div>

                  <input
                    type="text"
                    name="phone_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="form-field-hidden"
                  />

                  <button
                    type="submit"
                    disabled={formStatus === 'submitting'}
                    className="w-full py-3 bg-primary-500 hover:bg-primary-600 disabled:bg-primary-300 text-background-50 rounded-lg font-label font-medium text-sm transition-all duration-200 whitespace-nowrap"
                  >
                    {formStatus === 'submitting' ? (
                      <span className="flex items-center justify-center gap-2">
                        <i className="ri-loader-4-line animate-spin"></i>
                        发送中...
                      </span>
                    ) : (
                      '发送消息'
                    )}
                  </button>

                  {formStatus === 'success' && (
                    <p className="text-sm text-center text-green-600 bg-green-50 py-2.5 px-4 rounded-lg">
                      <i className="ri-check-line mr-1"></i>
                      消息已发送成功，我们会尽快回复您！
                    </p>
                  )}
                  {formStatus === 'error' && (
                    <p className="text-sm text-center text-red-500 bg-red-50 py-2.5 px-4 rounded-lg">
                      <i className="ri-error-warning-line mr-1"></i>
                      {formError}
                    </p>
                  )}
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'AboutPage',
        name: '关于密桃视频',
        description: '密桃视频是国内领先的视频内容聚合平台，聚合红桃视频、樱桃视频、茄子视频、糖心视频等热门站高清内容。',
        url: 'https://www.mitaovideo.com/about',
        isPartOf: {
          '@type': 'WebSite',
          name: '密桃视频',
          url: 'https://www.mitaovideo.com/',
        },
      }) }} />

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: '密桃视频',
        url: 'https://www.mitaovideo.com/',
        description: '密桃视频聚合红桃视频、樱桃视频、茄子视频、糖心视频等热门站高清内容，免费全集在线观看不卡顿。',
        email: contact.email,
        telephone: contact.phone,
        address: {
          '@type': 'PostalAddress',
          streetAddress: contact.address,
          addressLocality: '北京',
          addressRegion: '朝阳区',
          addressCountry: 'CN',
        },
        contactPoint: {
          '@type': 'ContactPoint',
          telephone: contact.phone,
          email: contact.email,
          contactType: 'customer service',
          availableLanguage: 'Chinese',
        },
      }) }} />

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.mitaovideo.com/' },
          { '@type': 'ListItem', position: 2, name: '关于我们' },
        ],
      }) }} />
    </div>
  );
}